import { Link, useLocation } from '@tanstack/react-router';
import { CirclePlus, PackagePlus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '../store/auth.store';
import { Header } from './Header';
import { BottomNav } from './BottomNav';
import Endpoint from '../components/svg/Endpoint.svg?react';
import Tenant from '../components/svg/Tenant.svg?react';
import Plans from '../components/svg/Plans.svg?react';
import Team from '../components/svg/Team.svg?react';
import Impersonate from '../components/svg/Impersonate.svg?react';

type NavItem = {
  label: string;
  path: string;
  icon: LucideIcon | any;
  adminOnly?: boolean;
};

const navGroups: { label: string; items: NavItem[] }[] = [
  {
    label: 'TENANTS',
    items: [
      { label: 'All Tenants', path: '/', icon: Tenant },
      {
        label: 'Plans & Billing',
        path: '/plans',
        icon: Plans,
        adminOnly: true,
      },
    ],
  },
  {
    label: 'OPERATIONS',
    items: [
      { label: 'New Tenant', path: '/customers/new', icon: CirclePlus },
      {
        label: 'Impersonate',
        path: '/impersonate',
        icon: Impersonate,
        adminOnly: true,
      },
      {
        label: 'S3 Endpoints',
        path: '/s3-endpoints',
        icon: Endpoint,
        adminOnly: true,
      },
      {
        label: 'Team & Access',
        path: '/team',
        icon: Team,
        adminOnly: true,
      },
    ],
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.role);
  const { pathname } = useLocation();

  return (
    <div className="bg-bg flex h-full">
      <nav className="border-primary/12 hidden w-16 shrink-0 flex-col gap-1 border-r md:flex lg:w-52">
        {/* Logo  */}
        <div className="border-primary/12 flex h-16 items-center justify-center border-b px-2 py-6">
          <span className="text-primary hidden text-xs font-semibold uppercase lg:block">
            Netsuite AI Assistant
          </span>
          <span className="text-primary block text-xs font-semibold uppercase lg:hidden">
            N
          </span>
        </div>
        <div className="flex flex-col gap-4 px-2 py-4">
          {navGroups.map((group) => (
            <div key={group.label}>
              <div className="text-subtle mb-1 hidden px-1 text-xs font-semibold tracking-widest uppercase lg:block">
                {group.label}
              </div>
              {group.items.map((item) => {
                if (item.adminOnly && role !== 'admin') return null;
                const active = pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={[
                      'flex items-center gap-2 rounded px-4 py-2 text-xs transition-colors',
                      active
                        ? 'text-muted bg-accent/12'
                        : 'text-primary/12 hover:bg-[--color-border]',
                    ].join(' ')}
                  >
                    <Icon width={14} height={14} className="hidden lg:block" />
                    <Icon width={24} height={24} className="block lg:hidden" />
                    <span className="hidden lg:block">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      </nav>
      <main className="flex min-h-0 flex-1 flex-col overflow-auto bg-[--color-surface-elevated]">
        <Header />
        <div className="flex-1 overflow-auto">{children}</div>
      </main>
      <BottomNav />
    </div>
  );
}
